import * as customValidation from './form-helpers/custom-validation-rules';

/**
 * Form element functionality
 */
export default class Form {
  /**
   * Creates Form instance
   *
   * @param {DomElement} form Form element
   */
  constructor(form) {
    // Form
    this.elements = {
      form,
      formFields: form.querySelectorAll('.js-form__field'),
      formError: form.querySelector('.js-form__error-msg'),
      tokenField: form.querySelector('[name="stage_token"]'),
      copyAddressField: form.querySelector('.js-donation__field-copy-address'),
      submit: form.querySelector('[type="submit"]') || form.querySelector('.js-form__submit')
    };

    // Classes
    this.classes = {
      form: {
        FIELD: 'js-form__field',
        FIELDSET: 'form__fieldset',
        WRAPPER: 'form__field-wrapper',
        cellType: {
          DEFAULT: 'form__cell-type',
          DROPDOWN: 'form__cell-type--dropdown',
        },
        fieldType: {
          BIRTHDAY: 'form__field-text--birthday',
          TEXT: 'form__field-text',
          TEXTAREA: 'form__field-textarea',
          CHECKBOX: 'form__field-checkbox',
          EMAIL: 'js-form__email',
          EMAIL_CONFIRMATION: 'js-form__email-confirmation',
          RADIO: 'form__field-radio',
          SELECT: 'form__field-select',
          SYRUP: 'js-form__syrup'
        },
      },
      validation: {
        ERROR: 'error',
        SUCCESS: 'success',
      },
      stage: {
        ERROR: 'stage-error',
        FIRST_REQUIRED: 'stage-first-required',
      },
    };

    this.validationTimer;
    this.validationDelay = 1000;//milliseconds

    // Bind events to form elements
    this.bindEvents();
    this.tokenFetched = false;
  }

  /**
   * Binds validation function to form field.
   *
   * @param {DomElement} field Form field input
   */
  bindValidationToFieldEvents(field) {
    // Check that element is form field
    if (field.classList.contains('js-form__field')) {
      // Forwards different events to validation function
      if (['INPUT', 'TEXTAREA'].includes(field.tagName)) {
        //validate field after user has paused for x milliseconds
        field.addEventListener('input', () => {
          clearTimeout(this.validationTimer); // Clear timer
          this.validationTimer = setTimeout(() => {
            this.validateFieldHandler(field);
          }, this.validationDelay);
        });
        field.addEventListener('blur', () => this.validateFieldHandler(field));
      } else if (field.tagName === 'SELECT') {
        field.addEventListener('change', () =>
          this.validateFieldHandler(field)
        );
      }
    }
  }

  /**
   * Field validation handler
   *
   * @param {DOMElement} field Form field input
   * @returns {boolean} Field validation result
   */
  validateFieldHandler(field) {
    if ( this.elements.formError) {
      this.elements.formError.style.display = 'none';
    }
    // Get type for field
    const fieldTypeClass = this.getFieldTypeClass(field);

    // Check if fields is valid
    const isValid = this.isValid(field, fieldTypeClass);

    // Update field ui based on validation result
    this.updateFieldByValidity(field, isValid);

    return isValid;
  }

  /**
   * Checks field validity
   *
   * @param {DOMELement} field Form field element
   * @param {string} type Field type
   * @returns {boolean} Field validity
   */
  isValid(field, type) {
    // If checkValidity function is supported
    if (field.checkValidity) {
      return (
        field.checkValidity() && this.fieldTypeSpecificValidation(field, type) && this.checkCustomValidation(field)
      );
    }

    // if browser doesn't support checkValidity, allow anyway
    return true;
  }

  /**
   * Checks custom validations
   * @sample: <input data-validation='{"minAge": 18}'>
   * Ignores params for which a validation function is not found
   * @param {DOMElement} field Field to be validated
   * @returns {boolean} Field validity.
   */
  checkCustomValidation(field) {

    const validationRules = field.dataset.validation ? JSON.parse(field.dataset.validation) : false;
    if(typeof validationRules === 'object') {
      const validationStates = [];

      Object.keys(validationRules).forEach(rule => {
        if(rule === 'agreementCheck' && !this.emailIsValid) {
          if (this.emailField && this.emailField.isValid === false) {
            // validationStates.push(false)
          }
          // request email registry check
          // this.ajaxAgreementCheck(field, validationRules[rule])
        } else if(rule === 'mustMatch' && validationRules[rule] === 'email') {
          const originalEmailField = this.elements.form.querySelector(`.${this.classes.form.fieldType.EMAIL}`);
          const errorMsgContainer = field.parentNode.querySelector('.form__help-text--error');
          // Invalid if empty or the values do not match
          if(!field.value) {
            const errorMsg = field.dataset.errorEmpty || '';
            if(errorMsgContainer) {
              errorMsgContainer.innerHTML = errorMsg;
            }
            validationStates.push(false)
          } else if (originalEmailField && field.value !== originalEmailField.value) {
            validationStates.push(false)
            const errorMsg = field.dataset.errorMismatch || '';
            if(errorMsgContainer) {
              errorMsgContainer.innerHTML = errorMsg;
            }
          }
        } else if(rule === 'mustMatch' && validationRules[rule] === 'emailConfirmation') {
          const confirmEmailField = this.elements.form.querySelector(`.${this.classes.form.fieldType.EMAIL_CONFIRMATION}`);
          // If the user fixes a typo in the first email field, force the confirm field to re-check itself
          if (confirmEmailField && confirmEmailField.value.length > 0) {
            // Use a slight delay to avoid maximum call stack issues during fast typing
            setTimeout(() => this.validateFieldHandler(confirmEmailField), 50);
          }
        } else if(customValidation[rule]) {
          validationStates.push(customValidation[rule](field.value, validationRules[rule]));
        }
      });

      if(validationStates.some(val => val === false)) {
        return false;
      }
    }
    return true;
  }
  /**
   * Prepares for a safe and timely email check
   * @param {DOMElement} field Field to be validated
   * @param {string} url fetch url
   */
  ajaxAgreementCheck(field, url) {
    if (document.activeElement === field || this.emailField?.value === field.value) {
      // 'Too early to check OR no need to check the same value
      return
    }
    this.updateToken().then(() => this.doCheckAgreement(field, url));
  }

  /**
   * Checks if email is already registered
   * @param {DOMElement} field Field to be validated
   * @param {string} url fetch url
   */
  doCheckAgreement = async (field, url) => {
    const email = field.value;

    this.emailField = {
      value: email,
      isValid: false
    }
    const data = new FormData();
    data.append('email', email);
    data.append('token', this.elements.tokenField.value);

    try {
      const res = await fetch(url, {
        method: 'POST',
        body: data
      });

      const response = await res.json();
      const errorTextField = field.parentNode?.querySelector('.form__help-text--error');

      if (response.status === 'error') {
        this.emailField.isValid = false;

        if (errorTextField && response.message) {
          errorTextField.innerHTML = response.message;
          this.updateFieldByValidity(field, false);
        }
      } else {
        this.emailField.isValid = true;
        this.updateFieldByValidity(field, true);
      }
    } catch(error) {
      console.warn('Email validation failed');
    }

    // reset token state
    this.tokenFetched = false;
  }


  /**
   * Type specific validation for fields. Only some elements needs custom validations.
   *
   * @param {DOMElement} field Field to be validated
   * @param {string} type Field type
   * @returns {boolean} Field validity. If type doesn't have validation this is always true.
   */
  fieldTypeSpecificValidation(field, type) {
    let isValid = true;

    // Checkbox
    if (type === this.classes.form.fieldType.CHECKBOX) {
      // If checkbox is required
      if (field.classList.contains(this.classes.stage.FIRST_REQUIRED)) {
        // Get wrapping fieldset of checbkoxes
        const fieldset = field.closest(`.${this.classes.form.FIELDSET}`);

        // Get all checkboxes inside fieldset
        const checkboxes = fieldset.querySelectorAll('input');
        const checkedCheckboxes = [...checkboxes].filter(
          (checkbox) => checkbox.checked
        );

        // Invalid if not checked at least one
        if (checkedCheckboxes.length === 0) {
          isValid = false;
        }
      }

    // Select
    } else if (type === this.classes.form.fieldType.SELECT) {
      // If value is empty it's not selected
      if ((field.getAttribute('required') || field.classList.contains('is-required')) && field.value === '') {
        isValid = false;
      }
    }

    return isValid;
  }

  /**
   * Gets field type identifier class from field element
   *
   * @param {DOMELement} field Form field element
   * @returns {string|undefined} If field type found class return it. If nothing found return undefined.
   */
  getFieldTypeClass(field) {
    if (field.classList.contains(this.classes.form.fieldType.TEXT)) {
      return this.classes.form.fieldType.TEXT;
    } else if (field.classList.contains(this.classes.form.fieldType.TEXTAREA)) {
      return this.classes.form.fieldType.TEXTAREA;
    } else if (field.classList.contains(this.classes.form.fieldType.CHECKBOX)) {
      return this.classes.form.fieldType.CHECKBOX;
    } else if (field.classList.contains(this.classes.form.fieldType.RADIO)) {
      return this.classes.form.fieldType.RADIO;
    } else if (field.classList.contains(this.classes.form.fieldType.SELECT)) {
      return this.classes.form.fieldType.SELECT;
    }
  }

  /**
   * Update field classes by validity
   *
   * @param {DOMELement} field Form field element
   * @param {boolean} isValid Field validity
   */
  updateFieldByValidity(field, isValid) {
    let fieldTypeClass = this.getFieldTypeClass(field);

    // Update ui only if type is detected
    if (typeof fieldTypeClass !== 'undefined') {
      // Element where error classes are updated
      let elementForValidityClasses = field.closest(
        `.${this.classes.form.WRAPPER}`
      );

      let validationClassPrefix = this.classes.form.WRAPPER;

      // Checkbox
      if (fieldTypeClass === this.classes.form.fieldType.CHECKBOX) {
        const fieldset = field.closest(
          `.${this.classes.form.FIELDSET}`
        );
        if(fieldset) {
          elementForValidityClasses = fieldset;
          validationClassPrefix = fieldTypeClass;
        }
        // Select
      } else if (fieldTypeClass === this.classes.form.fieldType.SELECT) {
        // Get closest form cell. Because of Crasman Multiselect html modifications validation classes are added to cell wrapper.
        elementForValidityClasses = field.closest(
          `.${this.classes.form.cellType.DROPDOWN}`
        );
        validationClassPrefix = this.classes.form.cellType.DEFAULT;
      }

      //if wrapper is found
      if(elementForValidityClasses) {
        // Create validation classes
        let validClass = [validationClassPrefix, this.classes.validation.SUCCESS].join(
          '--'
        );
        let errorClass = [validationClassPrefix, this.classes.validation.ERROR].join(
          '--'
        );

        // Update validation classes
        elementForValidityClasses.classList.add(
          isValid ? validClass : errorClass
        );
        elementForValidityClasses.classList.remove(
          isValid ? errorClass : validClass
        );
      }
    }
  }

  /**
   * Before submit handler. Prevents default form submit event. Submits only if form ui validation passes
   *
   * @param {Event} event Submit event
   * @returns {boolean} was submission succesfull
   */
  beforeSubmit(event) {
    event.preventDefault();

    // Holds validation results for fields
    const fieldValidationResults = [];

    // Some fields validation event might not have occurred so do validation for all fields
    const fields = this.elements.formFields;

    // Show validation status on ui for each field
    fields.forEach((field) => {
      const fieldIsValid = this.validateFieldHandler(field);
      fieldValidationResults.push(fieldIsValid);
    });

    // If some field does have error don't submit
    if (!fieldValidationResults.includes(false)) {
      this.submit();
      if(this.elements.formError) {
        this.elements.formError.style.display = 'none';
      }
      return true;
    } else {
      console.warn('Field validation failed, form was not submitted.');
      if(this.elements.formError) {
        this.elements.formError.style.display = 'block';
      }
      return false;
    }
  }

  /**
   * Submits form when validation passes. Can be overridden if subclass requires more complex submit logic.
   */
  submit() {
    // if reCaptcha is used it will submit the form
    const doSubmit = () => window.grecaptcha || this.elements.form.submit();
    if(this.tokenFetched || !this.elements.tokenField) {
      doSubmit();
    }
    else {
      this.elements.submit.disabled = true;
      this.updateToken().then((response) => {
        doSubmit();
      }).catch(error => {
        console.warn('Something went wrong. Token was not updated');
        this.elements.submit.disabled = false;
      })
    }
  }

  /**
   * Fetch and update new stage token
   * @returns {object} request response or rejected promise
   */
  updateToken() {
    return new Promise((resolve, reject) => {
      fetch('/stage-ajax/get-token/?v=3', {
        method: 'POST'
      })
        .then(response => response.text())
        .then(response => {
          this.elements.tokenField.value = response;
          this.tokenFetched = true;
          resolve(response);
        })
        .catch(error => {
          reject(error)
        })
    });
  }

  /**
   * Binds events to form and fields.
   */
  bindEvents() {
    // Bind validation to each field
    this.elements.formFields.forEach((field) => {
      this.bindValidationToFieldEvents(field);
    });

    if (this.elements.copyAddressField) {
      this.elements.copyAddressField.addEventListener('change', (e) => this.toggleCopyFields(e, ['address', 'postal_code', 'post_office']))
    }

    // Bind before submit event to form
    this.elements.submit.addEventListener('click', (event) => {
      this.beforeSubmit(event)
    });
  }

  /**
   * try to prevent unwanted posting
   */
  initSyrupFields() {
    let fieldIsShown = false;

    const syrupFields = this.elements.form.querySelectorAll(`.${this.classes.form.fieldType.SYRUP}`);
    if (!syrupFields.length) {
      return;
    }

    //after any human interaction and create syrup fields
    ['click','ontouchstart', 'mousedown', 'focus'].forEach((event) => {
      this.elements.form.addEventListener(event, () => {
        if (!fieldIsShown) {
          syrupFields.forEach((field) => {
            const input = document.createElement('input');
            input.setAttribute('type', 'hidden');
            input.setAttribute('value', 'u');
            input.setAttribute('name', field.dataset.name);
            this.elements.form.insertBefore(input, field);
          });
        }
        fieldIsShown = true;
      }, { once: true });
    });
  }

  /**
   * Init functionality for sections which visibility depends on specific input status
   */
  initFormConditionals() {
    //Donation landing forms
    const conditionalFields = document.querySelectorAll('.js-show-form-content');
    if (conditionalFields.length) {
      import('./form-helpers/form-with-conditionals')
        .then(({default: FormWithConditionals}) => {
          new FormWithConditionals(this.elements.form, {}).init();
        });
    }
  }

  /**
   * Allow copying data from previously filled fields to new fields
   * @param {Event} event change event
   * @param {array} fields fields to copy
   */
  toggleCopyFields(event, fields) {
    if (!fields) return

    fields.forEach(field => {
      const fieldFromCopy = this.elements.form.querySelector(`[name="${field}"]`);
      const fieldToCopy = this.elements.form.querySelector(`.js-form__copy-${field}`);
      if (fieldFromCopy && fieldToCopy) {
        fieldToCopy.value = event.target.checked ? (fieldFromCopy?.value || '') : '';
      }
    })

    const extraFieldsWrapper = this.elements.form.querySelector('.js-form__subscribe-fields');
    if (extraFieldsWrapper) {
      extraFieldsWrapper.setAttribute('aria-hidden', !!event.target.checked);
      extraFieldsWrapper.style.display = event.target.checked ? 'none' : 'initial';
    }
  }

  /**
   * Apply required rules from data-required-rules attribute.
   * Toggles required state on fields based on the value of a controlling field (e.g. radio group).
   *
   * Rules format: { "controlFieldName": { "value1": { "targetField": true/false }, "value2": { ... } } }
   */
  initRequiredRules() {
    const rules = this.elements.form.dataset.requiredRules
      ? JSON.parse(this.elements.form.dataset.requiredRules)
      : null;

    if (!rules) return;

    this.elements.formFields.forEach((field) => {
      const fieldName = field.getAttribute('name');
      if (!rules[fieldName]) return;

      field.addEventListener('change', () => {
        this.applyRequiredRules(field, rules[fieldName][field.value]);
      });

      // Apply current rules on init
      if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
      this.applyRequiredRules(field, rules[fieldName][field.value]);
    });
  }

  applyRequiredRules(field, fieldRules) {
    if (!fieldRules) return;

    // Only apply for checked radio/checkbox
    if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;

    const optionalIndicatorMarkup = this.elements.form.dataset.optionalIndicator || '';

    this.elements.formFields.forEach((targetField) => {
      const targetName = targetField.getAttribute('name');
      if (typeof fieldRules[targetName] !== 'boolean') return;

      const required = fieldRules[targetName];
      const cell = targetField.closest('.js-form__cell');

      targetField.required = required;

      if (cell) {
        cell.classList.toggle('is-required', required);

        const label = cell.querySelector('.js-form__label');
        if (label) {
          const existing = label.querySelector('.form__optional-indicator');
          if (required) {
            if (existing) existing.remove();
          } else if (!existing && optionalIndicatorMarkup) {
            label.insertAdjacentHTML('beforeend', optionalIndicatorMarkup);
          }
        }
      }
    });
  }

  /**
   * Initializes form
   */
  init() {
    this.initSyrupFields();
    this.initFormConditionals();
    this.initRequiredRules();
    // If form has error
    if (this.elements.form.classList.contains(this.classes.stage.ERROR)) {

      // On page load birthday fields are text inputs so placeholder is shown. On focus it is changed to date.
      // However after error submit field should have value and should already be date type.
      const birthdayFields = this.elements.form.querySelectorAll(
        `.${this.classes.form.fieldType.BIRTHDAY}`
      );
      birthdayFields.forEach((field) => {
        if(field.dataset.type === 'date') {
          field.type = 'date';
        }
      });

      // Show validation status on ui for each field
      this.elements.formFields.forEach((field) => {
        this.validateFieldHandler(field);
      });
    }
  }
}
